import { createTagsId } from "@repo/fika-shared/src/loader/tags-schema"
import type { WithTypename } from "@urql/exchange-graphcache"
import { graphql } from "@/graphql"
import {
  type GraphCacheOptimisticUpdaters,
  type GraphCacheUpdaters,
  type MutationCreateNoteResult,
  type MutationDeleteNoteResult,
  type MutationUpdateNoteResult,
  NoteTypeEnum,
} from "@/graphql/generated"
import type { DeepPartial } from "@/lib/urql-client"

const OptimisticPostNotesFragment = graphql(`
	fragment OptimisticPostNotesFragment on Post @_unmask {
		id
		relatedNotes { id }
	}
`)

const OptimisticNotePostsFragment = graphql(`
	fragment OptimisticNotePostsFragment on Note @_unmask {
		id
		posts { id }
	}
`)

export const noteOptimisticUpdaters = {
  createNote: (variables, cache) => {
    if (variables.postId) {
      const post = cache.readFragment(OptimisticPostNotesFragment, {
        __typename: "Post",
        id: variables.postId,
      })
      const existing = post?.relatedNotes ?? []
      cache.link({ __typename: "Post", id: variables.postId }, "relatedNotes", [
        { __typename: "Note", id: variables.id },
        ...existing.map(note => ({
          __typename: "Note" as const,
          id: note.id,
        })),
      ])
    }
    return {
      __typename: "MutationCreateNoteSuccess",
      data: {
        __typename: "Note" as const,
        id: variables.id,
        content: variables.content,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        type: variables.type ?? NoteTypeEnum.User,
        tags: {
          __typename: "Tags" as const,
          id: createTagsId("user_note", variables.id),
          tags: [],
        },
        posts: variables.postId
          ? [{ __typename: "Post" as const, id: variables.postId }]
          : [],
      },
    } satisfies DeepPartial<
      WithTypename<MutationCreateNoteResult>
    > as unknown as WithTypename<MutationCreateNoteResult>
  },
  updateNote: variables => {
    return {
      __typename: "MutationUpdateNoteSuccess",
      data: {
        __typename: "Note" as const,
        id: variables.id,
        content: variables.content,
        updatedAt: new Date().toISOString(),
      },
    } satisfies DeepPartial<
      WithTypename<MutationUpdateNoteResult>
    > as WithTypename<MutationUpdateNoteResult>
  },
  deleteNote: (variables, cache) => {
    cache.invalidate({ __typename: "Note", id: variables.id })
    const note = cache.readFragment(OptimisticNotePostsFragment, {
      __typename: "Note",
      id: variables.id,
    })
    for (const post of note?.posts ?? []) {
      const postData = cache.readFragment(OptimisticPostNotesFragment, {
        __typename: "Post",
        id: post.id,
      })
      const filtered = (postData?.relatedNotes ?? []).filter(
        n => n.id !== variables.id,
      )
      cache.link(
        { __typename: "Post", id: post.id },
        "relatedNotes",
        filtered.map(n => ({ __typename: "Note" as const, id: n.id })),
      )
    }
    return {
      __typename: "MutationDeleteNoteSuccess",
      data: true,
    } satisfies DeepPartial<
      WithTypename<MutationDeleteNoteResult>
    > as WithTypename<MutationDeleteNoteResult>
  },
} satisfies GraphCacheOptimisticUpdaters

export const noteMutationUpdaters = {
  createNote(_result, _args, cache) {
    cache
      .inspectFields("Query")
      .filter(f => f.fieldName === "latestPostNotes")
      .forEach(({ arguments: args }) => {
        cache.invalidate("Query", "latestPostNotes", args)
      })
  },
  deleteNote(_result, args, cache) {
    cache.invalidate({ __typename: "Note", id: args.id })
    cache
      .inspectFields("Query")
      .filter(f => f.fieldName === "latestPostNotes")
      .forEach(({ arguments: fieldArgs }) => {
        cache.invalidate("Query", "latestPostNotes", fieldArgs)
      })
  },
} satisfies NonNullable<GraphCacheUpdaters["Mutation"]>
